import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import axios from '@utils/axios';
import captureException from '@utils/captureException';
import {
  isDrumkitApiBaseRoutingActive,
  syncVestaAxiosApiBaseUrl,
} from '@utils/drumkitHostResolution';
import { isDev } from '@utils/isProd';
import { getMailboxEmailAddress } from '@utils/outlookMailbox';

import { Flex } from 'components/layout';

import DrumkitIntro from '../components/DrumkitIntro';
import AuthService from './AuthService';

// Mock function for Office.auth.getAccessToken
const mockGetAccessToken = async () => {
  return 'mockAccessToken';
};

export default function Login(): React.JSX.Element {
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const login = () => {
    setLoading(true);
    if (isDrumkitApiBaseRoutingActive()) {
      syncVestaAxiosApiBaseUrl(axios, getMailboxEmailAddress(), null);
    }
    // Conditionally call getAccessToken based on environment
    // If dev, use mockGetAccessToken
    // If staging or prod, use Office.auth.getAccessToken

    const accessTokenPromise = isDev()
      ? mockGetAccessToken()
      : Office.auth.getAccessToken({
          allowSignInPrompt: true,
          allowConsentPrompt: true,
        });
    accessTokenPromise
      .then((value) => {
        return axios.post('/user/login/microsoft', {
          // Microsoft requires that the API URL for Vesta (app ID: dfe5d4cc-66cd-4299-afa7-ed8ab8fb9fb8)
          // matches the URL listed in Azure (vesta.drumkit.ai), or Office.auth.getAccessToken()
          // fails with code 13006. There can be only one API URL per app, and it must be set to api://vesta.drumkit.ai
          // for prod (or api://vesta-staging.drumkit.ai for staging).
          ...(isDev()
            ? { devEmail: Office.context.mailbox.userProfile.emailAddress }
            : {}),
          accessToken: value,
        });
      })
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          AuthService.setCurrentUser({
            ...res.data,
            api_base_url_at_issue: String(axios.defaults.baseURL ?? ''),
          });
          navigate('/home');
        }
      })
      .catch((err) => {
        console.log(err);
        if (err.response?.status === 404) {
          navigate('/signup');
        } else {
          captureException(err, { functionName: 'login' });
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <Flex direction='col' align='center'>
      <DrumkitIntro />
      <button
        type='button'
        onClick={login}
        className='bg-brand rounded-full shadow-md cursor-pointer mt-16 py-2 px-8 text-neutral-50 hover:bg-brand-600'
      >
        {loading ? 'Loading...' : 'Sign in to Drumkit'}
      </button>
    </Flex>
  );
}
